<template>
    <div class="package-pitch">

        <div class="pitch-panel muted" v-if="scaleLoading">
            <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Checking for your district's salary schedule...
        </div>

        <div class="pitch-panel" v-else-if="!hasDistrict">
            <div class="pitch-line"><i class="fad fa-stairs mr-2"></i>Each district has its own salary schedule.</div>
            <div class="pitch-note">Select your district above — when we have its schedule on file, we'll show you exactly what moving up is worth.</div>
        </div>

        <div class="pitch-panel" v-else-if="!scale">
            <div class="pitch-line"><i class="fad fa-stairs mr-2"></i>We don't have {{ districtName }}'s current salary schedule on file yet.</div>
            <div class="pitch-note">You can still size your bundle above — clock hours always count toward your 5-year certificate renewal and salary column advancement.</div>
        </div>

        <div class="pitch-panel" v-else-if="!position">
            <div class="pitch-line"><i class="fad fa-stairs mr-2"></i>We have {{ districtName }}'s {{ yearLabel }} salary schedule.<span class="stale-tag" v-if="stale">Prior years</span></div>
            <div class="pitch-note">Tell us your degree, current column, and years of experience above and we'll show you exactly what moving up is worth.</div>
        </div>

        <div class="pitch-panel is-active" v-else>
            <div class="pitch-header">
                <div class="pitch-district">{{ districtName }} &middot; {{ yearLabel }} salary schedule<span class="stale-tag" v-if="stale">Prior years</span></div>
            </div>

            <div class="pitch-section">
                <div class="pitch-heading">Here is where you are</div>
                <div class="pitch-current">
                    <span class="lane-chip">{{ position.current.tier }}</span>
                    <span class="pitch-salary">{{ money(position.currentSalary) }}<span class="per-year"> / yr</span></span>
                    <span class="pitch-meta">at {{ position.exp }} {{ position.exp === 1 ? 'year' : 'years' }} of experience</span>
                </div>
            </div>

            <div class="pitch-section" v-if="target">
                <div class="pitch-heading">Here is where you could be</div>
                <div class="pitch-note" v-if="shortfall > 0">
                    Your {{ pkgHours }}-hour bundle covers {{ pkgHours / 10 }} of the {{ target.requiredHours / 10 }} credits to {{ target.tier }} — {{ shortfall }} more hours gets you all the way. The chart shows the full move.
                </div>

                <!-- Salary projection: stay vs. advance -->
                <div class="chart-block">
                    <div class="chart-legend">
                        <span class="legend-item">
                            <svg width="22" height="8" aria-hidden="true"><line x1="1" y1="4" x2="21" y2="4" stroke="#a86d24" stroke-width="2" stroke-dasharray="5 4"/></svg>
                            Stay at {{ position.current.tier }}
                        </span>
                        <span class="legend-item">
                            <svg width="22" height="8" aria-hidden="true"><line x1="1" y1="4" x2="21" y2="4" stroke="#2f66a8" stroke-width="2"/></svg>
                            Move to {{ target.tier }}
                        </span>
                    </div>
                    <div class="chart-wrap" @mouseleave="lineHover = null">
                        <svg :viewBox="'0 0 ' + line.W + ' ' + line.H" preserveAspectRatio="xMidYMid meet"
                             @mousemove="onLineMove($event)">
                            <g v-for="tick in line.ticks" :key="'lt-' + tick.value">
                                <line :x1="line.m.l" :x2="line.W - line.m.r" :y1="tick.y" :y2="tick.y" stroke="#dde7f1" stroke-width="1"/>
                                <text :x="line.m.l - 8" :y="tick.y + 3.5" text-anchor="end" class="axis-text">{{ money(tick.value) }}</text>
                            </g>
                            <text v-for="(label, i) in line.xLabels" :key="'lx-' + i" :x="line.x(i)" :y="line.H - 6" text-anchor="middle" class="axis-text">{{ label }}</text>
                            <line v-if="lineHover !== null" :x1="line.x(lineHover)" :x2="line.x(lineHover)" :y1="line.m.t" :y2="line.H - line.m.b" stroke="#7a8a9d" stroke-width="1"/>
                            <path :d="line.stayPath" fill="none" stroke="#a86d24" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round"/>
                            <path :d="line.movePath" fill="none" stroke="#2f66a8" stroke-width="2" stroke-linecap="round"/>
                            <g v-for="(point, i) in line.stayPoints" :key="'sp-' + i">
                                <circle :cx="point.x" :cy="point.y" r="4" fill="#a86d24" stroke="#f2f7fc" stroke-width="2"/>
                            </g>
                            <g v-for="(point, i) in line.movePoints" :key="'mp-' + i">
                                <circle :cx="point.x" :cy="point.y" r="4" fill="#2f66a8" stroke="#f2f7fc" stroke-width="2"/>
                            </g>
                            <text :x="line.endLabels.stay.x" :y="line.endLabels.stay.y" class="end-text">{{ money(line.stayVals[line.stayVals.length - 1]) }}</text>
                            <text :x="line.endLabels.move.x" :y="line.endLabels.move.y" class="end-text is-strong">{{ money(line.moveVals[line.moveVals.length - 1]) }}</text>
                        </svg>
                        <div class="chart-tip" v-if="lineHover !== null" :style="lineTipStyle">
                            <div class="tip-title">{{ line.xLabels[lineHover] }} &middot; {{ position.exp + lineHover }} yrs of service</div>
                            <div>Move: <b>{{ money(line.moveVals[lineHover]) }}</b></div>
                            <div>Stay: <b>{{ money(line.stayVals[lineHover]) }}</b></div>
                            <div v-if="line.moveVals[lineHover] > line.stayVals[lineHover]" class="tip-gap">+{{ money(line.moveVals[lineHover] - line.stayVals[lineHover]) }} that year</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="pitch-section" v-if="target && waiting.total > 0">
                <div class="pitch-heading">Here is what waiting costs</div>
                <div class="pitch-note">Staying at {{ position.current.tier }} instead of moving to {{ target.tier }} gives up about <b class="loss">{{ money(waiting.total) }}</b> over the next five years.</div>

                <div class="chart-wrap" @mouseleave="barHover = null">
                    <svg :viewBox="'0 0 ' + bars.W + ' ' + bars.H" preserveAspectRatio="xMidYMid meet">
                        <line :x1="bars.m.l" :x2="bars.W - bars.m.r" :y1="bars.baseY" :y2="bars.baseY" stroke="#c4cfda" stroke-width="1"/>
                        <g v-for="(bar, i) in bars.items" :key="'bar-' + i"
                           @mouseenter="barHover = i" @mouseleave="barHover = null">
                            <rect :x="bar.hitX" :y="bars.m.t" :width="bars.hitW" :height="bars.baseY - bars.m.t" fill="transparent"/>
                            <path :d="bar.path" fill="#BA4D51" :opacity="barHover === null || barHover === i ? 1 : 0.55"/>
                            <text :x="bar.cx" :y="bars.H - 6" text-anchor="middle" class="axis-text">{{ bar.label }}</text>
                            <text v-if="i === bars.items.length - 1" :x="bar.cx" :y="bar.y - 6" text-anchor="middle" class="end-text is-loss">&minus;{{ money(bar.value) }}</text>
                        </g>
                    </svg>
                    <div class="chart-tip" v-if="barHover !== null" :style="barTipStyle">
                        <div class="tip-title">After {{ barHover + 1 }} {{ barHover === 0 ? 'year' : 'years' }}</div>
                        <div>That year: <b>{{ money(waiting.annual[barHover]) }}</b></div>
                        <div>Total given up: <b>{{ money(waiting.cumulative[barHover]) }}</b></div>
                    </div>
                </div>
            </div>

            <div class="pitch-section" v-if="!target">
                <div class="pitch-note"><i class="fas fa-trophy mr-2"></i>You're at the top column your current degree reaches on {{ districtName }}'s schedule — nice. Clock hours still count toward your 5-year certificate renewal.</div>
            </div>

            <div class="pitch-disclaimer">
                For informational purposes only. Estimates come from the district's published schedule and assume one additional year of service each year, using the top published step once you reach it.
                <template v-if="stale"> This is the {{ yearLabel }} schedule — the most recent we have on file — so current-year amounts may differ.</template>
                Compensation is subject to change from year to year — schedules are renegotiated regularly, and often go up.
                Actual placement and pay are determined by your district — always check with your district.
                <a v-if="scale.source_url" :href="scale.source_url" target="_blank" rel="noopener">Source</a>
            </div>
        </div>

        <!-- Shown when the schedule is missing OR outdated. The link/file is
             stored for staff to process by hand — never fetched or parsed
             automatically -->
        <div class="pitch-panel" v-if="showCbaAsk">
            <div class="cba-ask" v-if="!cbaSubmitted">
                <template v-if="scale">
                    <div class="cba-label"><i class="far fa-file-magnifying-glass mr-2"></i>Have a newer schedule? Help us update our copy.</div>
                    <div class="cba-text">Our copy of {{ districtName }}'s schedule is from {{ yearLabel }}. Please share the district's current Collective Bargaining Agreement (CBA) or salary schedule. It's important to share the source document itself — that's what we reference. A link and a copy of the file are both helpful, so share both if you can.</div>
                </template>
                <template v-else>
                    <div class="cba-label"><i class="far fa-file-magnifying-glass mr-2"></i>Know where to find it? Help us get it on file.</div>
                    <div class="cba-text">Please share your district's Collective Bargaining Agreement (CBA) or current salary schedule. It's important to share the source document itself — that's what we reference to build your district's schedule. A link and a copy of the file are both helpful, so share both if you can.</div>
                </template>
                <div class="cba-controls">
                    <b-input v-model="cbaUrl" size="is-small" expanded placeholder="Link to the document (https://...)" @keyup.enter.native="submitCba"/>
                    <b-button type="is-primary" size="is-small" :disabled="!cbaReady || cbaSubmitting" @click="submitCba">
                        <i class="fas fa-spin fa-spinner mr-2" v-if="cbaSubmitting"></i>Share
                    </b-button>
                </div>
                <div class="cba-upload">
                    <b-upload v-model="cbaFile" :accept="cbaAccept" @input="checkCbaFile">
                        <a class="upload-link"><i class="far fa-paperclip mr-1"></i>Attach the document</a>
                    </b-upload>
                    <span class="file-chip" v-if="cbaFile">
                        <i class="far fa-file mr-1"></i>{{ cbaFile.name }}
                        <a class="file-clear" @click="cbaFile = null"><i class="fas fa-times"></i></a>
                    </span>
                </div>
            </div>
            <div class="cba-ask is-done" v-else>
                <div class="cba-label"><i class="fas fa-check mr-2"></i>Thank you — we have your document.</div>
                <div class="cba-text">We'll review it and work on getting {{ districtName }}'s {{ scale ? 'schedule up to date' : 'schedule on file' }}.</div>
            </div>
        </div>

    </div>
</template>

<script>
    import {client as http} from "@/http_client";
    import {salaryAt, isStaleScale, FUTURE_YEARS} from "./scaleMath";

    export default {
        name: "PackagePitch",
        props: {
            scale: {type: Object, default: null},
            scaleLoading: {type: Boolean, default: false},
            hasDistrict: {type: Boolean, default: false},
            districtId: {type: Number, default: null},
            districtName: {type: String, default: ''},
            position: {type: Object, default: null},
            target: {type: Object, default: null},   // the lane the charts show
            pkgHours: {type: Number, default: 0}
        },
        data: function() {
            return {
                lineHover: null,
                barHover: null,
                cbaUrl: '',
                cbaFile: null,
                cbaAccept: '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp',
                cbaSubmitting: false,
                cbaSubmitted: false
            };
        },
        computed: {
            cbaReady() {
                return !!this.cbaFile || /^https?:\/\/\S+\.\S+/i.test(this.cbaUrl.trim());
            },
            yearLabel() {
                if (!this.scale) return '';
                return this.scale.start_year + '-' + this.scale.end_year;
            },
            stale() {
                return isStaleScale(this.scale);
            },
            // Missing or outdated schedule — either way, invite the document
            showCbaAsk() {
                return this.hasDistrict && !this.scaleLoading && (!this.scale || this.stale);
            },
            shortfall() {
                if (!this.target || !this.pkgHours) return 0;
                return Math.max(0, this.target.requiredHours - this.pkgHours);
            },
            // Both futures, year by year: this schedule year plus four more.
            // The advance path starts where you are and jumps once the move is made.
            projection() {
                if (!this.position || !this.target) return null;
                const stay = [], move = [], labels = [];
                for (let y = 0; y <= FUTURE_YEARS; y++) {
                    stay.push(salaryAt(this.position.current, this.position.exp + y));
                    move.push(y === 0 ? this.position.currentSalary : salaryAt(this.target.lane, this.position.exp + y));
                    labels.push((this.scale.start_year + y) + '-' + String(this.scale.end_year + y).slice(-2));
                }
                return {stay, move, labels};
            },
            waiting() {
                if (!this.projection) return {annual: [], cumulative: [], total: 0};
                const annual = [], cumulative = [];
                let running = 0;
                for (let y = 1; y <= FUTURE_YEARS; y++) {
                    const diff = this.projection.move[y] - this.projection.stay[y];
                    annual.push(diff);
                    running += diff;
                    cumulative.push(running);
                }
                return {annual, cumulative, total: running};
            },
            line() {
                const W = 640, H = 210, m = {l: 66, r: 76, t: 14, b: 26};
                const p = this.projection;
                if (!p) return {W, H, m, ticks: [], xLabels: [], stayPath: '', movePath: '', stayPoints: [], movePoints: [], stayVals: [], moveVals: [], x: () => 0, endLabels: {stay: {}, move: {}}};

                const all = [...p.stay, ...p.move];
                const lo = Math.min(...all), hi = Math.max(...all);
                const pad = Math.max((hi - lo) * 0.08, 500);
                const min = lo - pad, max = hi + pad;

                const x = i => m.l + (i / FUTURE_YEARS) * (W - m.l - m.r);
                const y = v => m.t + (1 - (v - min) / (max - min)) * (H - m.t - m.b);

                const toPoints = vals => vals.map((v, i) => ({x: x(i), y: y(v)}));
                const toPath = points => points.map((pt, i) => (i === 0 ? 'M' : 'L') + pt.x.toFixed(1) + ',' + pt.y.toFixed(1)).join(' ');

                const stayPoints = toPoints(p.stay);
                const movePoints = toPoints(p.move);
                const ticks = this.niceTicks(min, max, 3).map(v => ({value: v, y: y(v)}));

                // End labels: nudge apart if the lines finish close together
                const endLabels = {
                    stay: {x: x(FUTURE_YEARS) + 10, y: stayPoints[FUTURE_YEARS].y + 4},
                    move: {x: x(FUTURE_YEARS) + 10, y: movePoints[FUTURE_YEARS].y + 4}
                };
                if (Math.abs(endLabels.stay.y - endLabels.move.y) < 14) {
                    endLabels.move.y = endLabels.stay.y - 14;
                }

                return {W, H, m, x, ticks, xLabels: p.labels, stayPath: toPath(stayPoints), movePath: toPath(movePoints), stayPoints, movePoints, stayVals: p.stay, moveVals: p.move, endLabels};
            },
            bars() {
                const W = 640, H = 170, m = {l: 20, r: 20, t: 26, b: 26};
                const w = this.waiting;
                const baseY = H - m.b;
                if (!w.cumulative.length) return {W, H, m, baseY, items: [], hitW: 0};

                const max = Math.max(...w.cumulative) * 1.05;
                const slotW = (W - m.l - m.r) / FUTURE_YEARS;
                const barW = Math.min(46, slotW * 0.4);

                const items = w.cumulative.map((value, i) => {
                    const cx = m.l + slotW * i + slotW / 2;
                    const x0 = cx - barW / 2;
                    const top = m.t + (1 - value / max) * (baseY - m.t);
                    const r = Math.min(4, (baseY - top) / 2);
                    // rounded top corners only; the data-end is the top
                    const path = 'M' + x0 + ',' + baseY +
                        ' L' + x0 + ',' + (top + r) +
                        ' Q' + x0 + ',' + top + ' ' + (x0 + r) + ',' + top +
                        ' L' + (x0 + barW - r) + ',' + top +
                        ' Q' + (x0 + barW) + ',' + top + ' ' + (x0 + barW) + ',' + (top + r) +
                        ' L' + (x0 + barW) + ',' + baseY + ' Z';
                    return {value, cx, y: top, path, hitX: m.l + slotW * i, label: 'after ' + (i + 1) + (i === 0 ? ' year' : ' years')};
                });

                return {W, H, m, baseY, items, hitW: slotW};
            },
            lineTipStyle() {
                const percent = (this.line.x(this.lineHover) / this.line.W) * 100;
                return {left: Math.min(78, Math.max(8, percent)) + '%'};
            },
            barTipStyle() {
                const percent = (this.bars.items[this.barHover].cx / this.bars.W) * 100;
                return {left: Math.min(78, Math.max(8, percent)) + '%'};
            }
        },
        methods: {
            checkCbaFile(file) {
                if (file && file.size > 25 * 1024 * 1024) {
                    this.cbaFile = null;
                    this.$notify('error', 'File Too Large', 'Please choose a file under 25 MB.', {duration: 8000, permanent: false});
                }
            },
            submitCba() {
                if (!this.cbaReady || this.cbaSubmitting) return;
                this.cbaSubmitting = true;
                const form = new FormData();
                if (this.cbaUrl.trim()) form.append('url', this.cbaUrl.trim());
                if (this.districtId) form.append('district_id', this.districtId);
                if (this.cbaFile) form.append('file', this.cbaFile);
                http.post('app/users/cba-link', form).then(() => {
                    this.cbaSubmitting = false;
                    this.cbaSubmitted = true;
                }).catch(err => {
                    this.cbaSubmitting = false;
                    const serverMsg = err.response && err.response.data && err.response.data.error;
                    this.$notify('error', 'Could Not Share', serverMsg || 'Something went wrong sending your document. Please try again.', {duration: 8000, permanent: false});
                });
            },
            money(value) {
                return '$' + Math.round(value).toLocaleString('en-US');
            },
            niceTicks(min, max, count) {
                const span = max - min;
                const rawStep = span / (count + 1);
                const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
                const step = [1, 2, 5, 10].map(f => f * mag).find(s => s >= rawStep) || rawStep;
                const ticks = [];
                for (let v = Math.ceil(min / step) * step; v < max; v += step) ticks.push(v);
                return ticks;
            },
            onLineMove(event) {
                const rect = event.currentTarget.getBoundingClientRect();
                const vx = ((event.clientX - rect.left) / rect.width) * this.line.W;
                const {m, W} = this.line;
                const t = (vx - m.l) / (W - m.l - m.r);
                this.lineHover = Math.max(0, Math.min(FUTURE_YEARS, Math.round(t * FUTURE_YEARS)));
            }
        },
        watch: {
            target() {
                this.lineHover = null;
                this.barHover = null;
            },
            // A new district is a new ask
            districtId() {
                this.cbaUrl = '';
                this.cbaFile = null;
                this.cbaSubmitted = false;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .pitch-panel {
        margin-top: 0.4rem;
        background: #f2f7fc;
        border-radius: 6px;
        padding: 1rem 1.3rem;

        &.muted {
            color: #666d7a;
            font-size: 0.9rem;
        }

        .pitch-line {
            font-size: 0.95rem;
            color: #2e5f8f;
        }

        .stale-tag {
            display: inline-block;
            margin-left: 8px;
            vertical-align: 1px;
            background: #fdf3e3;
            border: 1px solid #ecd9b4;
            color: #8a6d3b;
            font-size: 0.62rem;
            font-weight: 900;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            padding: 1px 8px;
            border-radius: 999px;
        }

        .cba-ask {
            margin-top: 0.9rem;
            background: #ffffff;
            border: 1px solid #dbe5ee;
            border-radius: 6px;
            padding: 0.9rem 1.1rem;

            &.is-done {
                background: #f0f7f2;
                border-color: #cfe5d6;

                .cba-label {
                    color: #235f3c;
                }
            }

            .cba-label {
                font-size: 0.85rem;
                font-weight: 900;
                color: #33475b;
            }

            .cba-text {
                font-size: 0.82rem;
                color: #666d7a;
                margin-top: 0.3rem;
            }

            .cba-controls {
                display: flex;
                gap: 0.6rem;
                margin-top: 0.7rem;

                .control {
                    flex: 1;
                }
            }

            .cba-upload {
                margin-top: 0.5rem;
                display: flex;
                align-items: center;
                gap: 0.7rem;
                flex-wrap: wrap;

                .upload-link {
                    font-size: 0.8rem;
                    color: #2e5f8f;
                    text-decoration: underline;
                }

                .file-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: #f2f7fc;
                    border: 1px solid #dbe5ee;
                    border-radius: 999px;
                    padding: 2px 10px;
                    font-size: 0.78rem;
                    color: #33475b;
                    max-width: 100%;
                    overflow: hidden;

                    .file-clear {
                        color: #7a8a9d;
                        margin-left: 2px;

                        &:hover {
                            color: #c0392b;
                        }
                    }
                }
            }
        }

        .pitch-note {
            font-size: 0.85rem;
            color: #666d7a;
            margin-top: 0.3rem;

            .loss {
                color: #a83e33;
            }
        }

        &.is-active {
            border: 1px solid #d6e4f2;
        }

        .pitch-header {
            .pitch-district {
                font-size: 0.75rem;
                font-weight: bold;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                color: #7a8a9d;
            }
        }

        .pitch-section {
            margin-top: 0.9rem;

            .pitch-heading {
                font-size: 0.95rem;
                font-weight: 900;
                color: #33475b;
            }
        }

        .lane-chip {
            display: inline-block;
            background: #2e5f8f;
            color: #ffffff;
            font-size: 0.75rem;
            font-weight: bold;
            padding: 3px 10px;
            border-radius: 999px;
            white-space: nowrap;
        }

        .pitch-current {
            display: flex;
            align-items: baseline;
            gap: 0.8rem;
            margin-top: 0.5rem;
            flex-wrap: wrap;

            .pitch-salary {
                font-size: 1.3rem;
                font-weight: 900;
                color: #2e5f8f;

                .per-year {
                    font-size: 0.85rem;
                    font-weight: bold;
                    color: #666d7a;
                }
            }

            .pitch-meta {
                font-size: 0.85rem;
                color: #666d7a;
            }
        }

        .chart-block {
            margin-top: 0.8rem;
        }

        .chart-legend {
            display: flex;
            gap: 1.2rem;
            flex-wrap: wrap;
            margin-bottom: 0.3rem;

            .legend-item {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                font-size: 0.75rem;
                font-weight: bold;
                color: #666d7a;
            }
        }

        .chart-wrap {
            position: relative;
            margin-top: 0.3rem;

            svg {
                display: block;
                width: 100%;
                height: auto;
            }

            .axis-text {
                font-size: 11px;
                fill: #7a8a9d;
            }

            .end-text {
                font-size: 11px;
                font-weight: bold;
                fill: #666d7a;

                &.is-strong {
                    fill: #33475b;
                }

                &.is-loss {
                    fill: #a83e33;
                }
            }

            .chart-tip {
                position: absolute;
                top: 8px;
                background: #33475b;
                color: #ffffff;
                border-radius: 4px;
                padding: 0.4rem 0.6rem;
                font-size: 0.75rem;
                line-height: 1.1rem;
                pointer-events: none;
                white-space: nowrap;
                z-index: 5;

                .tip-title {
                    font-weight: bold;
                    margin-bottom: 2px;
                }

                .tip-gap {
                    color: #9fd8b1;
                    font-weight: bold;
                }
            }
        }

        .pitch-disclaimer {
            margin-top: 1rem;
            font-size: 0.7rem;
            line-height: 1rem;
            color: #7a8a9d;

            a {
                margin-left: 0.3rem;
            }
        }
    }
</style>
